// Angular modules.
import { Injectable } from '@angular/core';
import {
	INTERACTION_TYPE,
	ITEM_TYPE,
	SCOPE_TYPE,
	SUBSCOPE_TYPE,
} from 'app/interfaces/actiontracker.interface';
import { Subject } from 'rxjs';
// Zenklub interfaces.
import Talk from 'talkjs';
import { UserData } from 'talkjs/all';

import { environment } from '../../environments/environment';
import { IChatUser } from '../interfaces/chatuser.interface';
import { AccountService } from './account.service';
import { ActionTrackerService } from './actiontracker.service';
import { ContactsService } from './contacts.service';
import { UserService } from './user.service';

export enum ChatStatus {
	unloaded,
	loading,
	loaded,
	error,
}

@Injectable()
export class ChatService {
	public from: string;
	private currentUser: Talk.User;
	private inboxElement: HTMLElement;
	private isOpen = false;
	private notificationPermissionRequested = false;
	public displayModeSub = new Subject<string>();
	public displayMode: string;
	public inbox: Talk.Inbox;
	public session: Talk.Session;
	public status: ChatStatus = ChatStatus.unloaded;
	public switchState = new Subject<string>();
	public unreadMessage: Talk.UnreadConversation;
	public hasUnreadConversations = false;
	readonly appId = environment.talkjs.appId;
	readonly role = 'user';
	readonly specialistRole = 'specialist';
	readonly unreadTimeout = 5000;
	constructor(
		private accountService: AccountService,
		private contactsService: ContactsService,
		private userService: UserService,
		private atService: ActionTrackerService
	) {}

	async setChatOpen(
		isOpen: boolean,
		from: string,
		displayMode?: 'conversation' | 'list'
	) {
		this.from = from;
		if (isOpen) {
			await this.initChat();
			if (this.session) {
				await this.createInbox();
				this.requestDesktopNotifications();
			}
			this.atService.eventTracker(
				SCOPE_TYPE.Chat,
				SUBSCOPE_TYPE.Messages,
				displayMode === 'list' ? ITEM_TYPE.List : ITEM_TYPE.Conversation,
				INTERACTION_TYPE.Open,
				{
					from: this.from,
				}
			);
		} else {
			this.inbox?.select(null);
		}
		this.isOpen = isOpen;
		const chatEvent = new CustomEvent('zenEvents#ChatVisibility', {
			detail: {
				isOpen,
			},
		});

		document.dispatchEvent(chatEvent);

		return this.inbox;
	}

	get isChatOpen() {
		return this.isOpen;
	}

	// Initialize chat.
	public initChat(): Promise<Talk.Session> {
		return new Promise<Talk.Session>(async (resolve) => {
			if (this.session) {
				resolve(this.session);
				return;
			}
			const signature = (await this.accountService.getSignature())?.signature;
			this.status = ChatStatus.loading;

			try {
				if (!signature) {
					throw new Error('Without signature');
				}
				await Talk.ready;
				const zku = this.userService.getUserLocal();
				if (zku) {
					const user: IChatUser = {
						id: await this.userService.getUserId(),
						username: zku.name,
						email: zku.email,
						photoUrl: this.userService.getStoredUserPicture(),
						role: this.specialistRole,
					};

					this.currentUser = await this.createUser(user);
					this.session = new Talk.Session({
						appId: this.appId,
						me: this.currentUser,
						signature,
					});

					let loadedFirstTime = false;
					this.session.unreads.onChange((conversations) => {
						const hasUnreadMessages = conversations.length > 0;
						this.hasUnreadConversations = hasUnreadMessages;
						if (hasUnreadMessages && !this.isOpen && loadedFirstTime) {
							this.unreadMessage = conversations[0];
							setTimeout(() => {
								this.unreadMessage = null;
							}, this.unreadTimeout);
						}
						loadedFirstTime = true;
					});

					resolve(this.session);
				}
				this.status = ChatStatus.loaded;
			} catch (e) {
				console.error(e);
				this.status = ChatStatus.error;
				resolve(undefined);
			}
		});
	}

	async createUser(applicationUser: IChatUser) {
		await Talk.ready;
		return new Talk.User({
			id: applicationUser.id,
			email: applicationUser.email || null,
			name: applicationUser.username,
			photoUrl: this.getValidPhotoUrl(applicationUser.photoUrl),
			role: applicationUser.role,
		});
	}

	getValidPhotoUrl(photoUrl) {
		if (!photoUrl || photoUrl === this.contactsService.defaultImage) {
			return null;
		}
		return photoUrl;
	}

	async createInbox() {
		if (this.inbox) {
			return this.inbox;
		}
		this.inbox = this.session.createInbox({
			showChatHeader: false,
			showFeedHeader: false,
			showMobileBackButton: false,
		});

		// Make the backbutton work. Calling `select(null)` takes the user back to the feed view on narrow screens.
		document.getElementById('back-btn')?.addEventListener('click', () => {
			this.inbox.select(null);
		});
		this.inbox.select(null);
		this.inbox.onConversationSelected(({ others }) => {
			this.onConversationSelected(others);
		});
		this.inbox.onSendMessage(() => {
			this.atService.eventTracker(
				SCOPE_TYPE.Chat,
				SUBSCOPE_TYPE.Messages,
				ITEM_TYPE.Conversation,
				INTERACTION_TYPE.Sended,
				{
					from: this.from,
				}
			);
		});
		await this.inbox.mount(this.inboxElement);
		return this.inbox;
	}

	public onConversationSelected(others: UserData[]) {
		const chatHeader = document.getElementById('chatbox-header');
		const feedHeader = document.getElementById('feed-header');
		const title = document.getElementById('chat-title');
		if (others) {
			title.innerHTML = others.map((o) => o.name).join(', ');
			chatHeader.classList.remove('zk-hidden');
			feedHeader.classList.add('zk-hidden');
			this.inboxElement.classList.remove('chat-list');
			this.setChatOpen(true, this.from, 'conversation');
		} else {
			chatHeader.classList.add('zk-hidden');
			feedHeader.classList.remove('zk-hidden');
			this.inboxElement.classList.add('chat-list');
		}
	}

	async requestDesktopNotifications() {
		if (this.notificationPermissionRequested) {
			return;
		}
		if (
			this.browserNotificationsIs('default') ||
			this.browserNotificationsIs('granted')
		) {
			this.notificationPermissionRequested = true;
			await this.session.setDesktopNotificationEnabled(true);
		}
	}

	browserNotificationsIs(status: 'denied' | 'default' | 'granted') {
		return window['Notification']?.permission === status;
	}

	async startConversation(
		user: IChatUser,
		from: string,
		message?: string,
		displayMode?: string
	) {
		this.from = from;
		const session = await this.initChat();
		let inbox: Talk.Inbox;

		if (displayMode) {
			this.displayModeSub.next(displayMode);
		}

		if (session) {
			inbox = await this.createInbox();
			const conversation = await this.getOrCreateConversation(
				session,
				user,
				message
			);
			inbox.select(conversation);
		} else {
			inbox = await this.setChatOpen(true, from, 'conversation');
		}

		return inbox;
	}

	private async getOrCreateConversation(
		session: Talk.Session,
		otherApplicationUser: any,
		message?: string
	) {
		const otherUser = await this.createUser(otherApplicationUser);
		const conversation = session.getOrCreateConversation(
			Talk.oneOnOneId(this.currentUser, otherUser)
		);
		conversation.setParticipant(this.currentUser);
		conversation.setParticipant(otherUser);
		if (message) {
			conversation.sendMessage(message);
		}
		return conversation;
	}

	public async openUnreadMessage() {
		const conversation = this.session.getOrCreateConversation(
			Talk.oneOnOneId(this.currentUser, this.unreadMessage.lastMessage.senderId)
		);
		this.unreadMessage = null;
		await this.createInbox();
		this.inbox.select(conversation);
	}

	public setInboxElement(element: HTMLElement) {
		this.inboxElement = element;
	}

	public logout() {
		this.inbox?.destroy();
		this.session?.destroy();
		this.inbox = null;
		this.session = null;
	}

	public callSwitch(state: string) {
		this.switchState.next(state);
		this.setChatOpen(false, 'list');
	}
}
